import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { Toaster } from "sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import RoleRedirect from "@/pages/RoleRedirect";
import ProtectedRoute from "@/components/ProtectedRoute";

import OfficerLayout from "@/components/layout/OfficerLayout";
import CitizenLayout from "@/components/layout/CitizenLayout";
import OfficerDashboard from "@/pages/dashboard/OfficerDashboard";
import CitizenDashboard from "@/pages/dashboard/CitizenDashboard";

import PropertyList from "@/pages/property/PropertyList";
import PropertyDetail from "@/pages/property/PropertyDetail";
import PropertyRegister from "@/pages/property/PropertyRegister";
import WaterList from "@/pages/water/WaterList";
import Pay from "@/pages/payment/Pay";
import Receipt from "@/pages/payment/Receipt";
import ReceiptList from "@/pages/ReceiptList";
import GISMap from "@/pages/gis/GISMap";
import Reports from "@/pages/reports/Reports";
import UserManagement from "@/pages/admin/UserManagement";
import AuditLogs from "@/pages/admin/AuditLogs";
import Complaints from "@/pages/Complaints";
import Profile from "@/pages/Profile";
import BuildingPlansList from "@/pages/building/BuildingPlansList";
import BuildingPlanNew from "@/pages/building/BuildingPlanNew";
import BuildingPlanDetail from "@/pages/building/BuildingPlanDetail";
import StateMISDashboard from "@/pages/dashboard/StateMISDashboard";
import Reconciliation from "@/pages/reconciliation/Reconciliation";
import SewerageList from "@/pages/sewerage/SewerageList";
import SewerageRegister from "@/pages/sewerage/SewerageRegister";
import SewerageDetail from "@/pages/sewerage/SewerageDetail";
import AdList from "@/pages/advertisement/AdList";
import AdRegister from "@/pages/advertisement/AdRegister";
import AdDetail from "@/pages/advertisement/AdDetail";
import LicenseList from "@/pages/license/LicenseList";
import LicenseNew from "@/pages/license/LicenseNew";
import LicenseDetail from "@/pages/license/LicenseDetail";

const OFFICER_ROLES = [
  "super_admin", "state_admin", "municipal_commissioner", "executive_officer",
  "revenue_officer", "cashier", "tax_inspector", "data_entry_operator", "auditor", "bank_admin",
];

function App() {
  React.useEffect(() => {
    if (localStorage.getItem("pulb_theme") === "dark") document.documentElement.classList.add("dark");
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ForgotPassword />} />
          <Route path="/app" element={<RoleRedirect />} />

          {/* Officer routes */}
          <Route element={<ProtectedRoute roles={OFFICER_ROLES}><OfficerLayout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<OfficerDashboard />} />
            <Route path="/properties" element={<PropertyList />} />
            <Route path="/properties/new" element={<PropertyRegister />} />
            <Route path="/properties/:pid" element={<PropertyDetail />} />
            <Route path="/water" element={<WaterList />} />
            <Route path="/sewerage" element={<SewerageList />} />
            <Route path="/sewerage/new" element={<SewerageRegister />} />
            <Route path="/sewerage/:cid" element={<SewerageDetail />} />
            <Route path="/advertisements" element={<AdList />} />
            <Route path="/advertisements/new" element={<AdRegister />} />
            <Route path="/advertisements/:aid" element={<AdDetail />} />
            <Route path="/licenses" element={<LicenseList />} />
            <Route path="/licenses/new" element={<LicenseNew />} />
            <Route path="/licenses/:lid" element={<LicenseDetail />} />
            <Route path="/payments" element={<Pay />} />
            <Route path="/receipts" element={<ReceiptList />} />
            <Route path="/receipts/:rid" element={<Receipt />} />
            <Route path="/gis" element={<GISMap />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/admin/users" element={<UserManagement />} />
            <Route path="/admin/audit" element={<AuditLogs />} />
            <Route path="/building-plans" element={<BuildingPlansList />} />
            <Route path="/building-plans/new" element={<BuildingPlanNew />} />
            <Route path="/building-plans/:aid" element={<BuildingPlanDetail />} />
            <Route path="/mis" element={<StateMISDashboard />} />
            <Route path="/reconciliation" element={<Reconciliation />} />
          </Route>

          {/* Citizen routes */}
          <Route element={<ProtectedRoute roles={["citizen"]}><CitizenLayout /></ProtectedRoute>}>
            <Route path="/portal" element={<CitizenDashboard />} />
            <Route path="/portal/pay" element={<Pay />} />
            <Route path="/portal/pay/:did" element={<Pay />} />
            <Route path="/portal/receipts" element={<ReceiptList />} />
            <Route path="/portal/complaints" element={<Complaints />} />
            <Route path="/portal/profile" element={<Profile />} />
            <Route path="/portal/building-plans" element={<BuildingPlansList />} />
            <Route path="/portal/building-plans/new" element={<BuildingPlanNew />} />
            <Route path="/portal/building-plans/:aid" element={<BuildingPlanDetail />} />
            <Route path="/portal/licenses" element={<LicenseList />} />
            <Route path="/portal/licenses/new" element={<LicenseNew />} />
            <Route path="/portal/licenses/:lid" element={<LicenseDetail />} />
            <Route path="/portal/advertisements" element={<AdList />} />
            <Route path="/portal/advertisements/new" element={<AdRegister />} />
            <Route path="/portal/advertisements/:aid" element={<AdDetail />} />
          </Route>

          {/* Shared for citizens too - property view for their own properties */}
          <Route element={<ProtectedRoute roles={["citizen"]}><CitizenLayout /></ProtectedRoute>}>
            <Route path="/portal/properties/:pid" element={<PropertyDetail />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
